import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  EmbeddedViewRef,
  Injectable,
  Injector,
  StaticProvider,
  Type,
} from '@angular/core';
import {
  ComponentContainer,
  ComponentItem,
  ContentItem,
  DragSource,
  EventEmitter,
  GoldenLayout,
  JsonValue,
  LayoutConfig,
  ResolvedComponentItemConfig,
  ResolvedLayoutConfig,
} from 'golden-layout';
import { v4 } from 'uuid';
import { IOpenWindow, IWindowType } from '../../_models/models';
import { WidgetConfig } from '../../_models/WidgetConfig';
import { GoldenLayoutContainerInjectionToken, WidgetHostDirective } from '../../_directives/widget-host.directive';
import { FileHelpers } from '../../_helpers/FileHelpers';
import BubblingEvent = EventEmitter.BubblingEvent;
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  shouldDisplayControls$ = new BehaviorSubject<boolean>(true);

  private readonly LOCALSTORAGE_CONFIG_KEY = 'mydashconfig';
  private goldenLayout!: GoldenLayout;
  private appRef!: ApplicationRef;
  private containerMap = new Map<ComponentContainer, ComponentRef<WidgetHostDirective>>();
  private dragSources: (DragSource | undefined)[] = [];
  private openedComponents = new Array<string>();
  private components = new Map<string, Type<any>>();
  private componentsConfig = new Map<string, WidgetConfig<any>>();
  private openedWindows: IOpenWindow = {};
  private isCurrentWindowApopup = false;

  private goldenLayoutBindComponentEventListener = (container: ComponentContainer, itemConfig: ResolvedComponentItemConfig) =>
    this.handleBindComponentEvent(container, itemConfig);
  private goldenLayoutUnbindComponentEventListener = (container: ComponentContainer) => this.handleUnbindComponentEvent(container);

  constructor(private componentFactoryResolver: ComponentFactoryResolver) {
    window.addEventListener(
      'message',
      (evt) => {
        console.debug('window message detected', evt.data.type, evt);

        const config: WidgetConfig<any> = evt.data.config;

        switch (evt.data.type) {
          case 'open_widget':
            this.goldenLayout.addComponent(evt.data.widgetType, { config }, config.title);            
            break;
          case 'popout_widget':
            this.isCurrentWindowApopup = true;
            config.parentWindow = evt.source as Window;
            this.goldenLayout.addComponent(evt.data.widgetType, { config }, config.title);

            this.shouldDisplayControls$.next(false);
            this.setSize(window.innerWidth, window.innerHeight);            
            break;
          case 'popin_widget':
            const defaultConfig = this.componentsConfig.get(evt.data.widgetType);
            if (defaultConfig) {
              if (this.isWidgetOpen(evt.data.widgetType)) {
                this.focusWidget(evt.data.widgetType);
              } else {
                this.goldenLayout.addComponent(evt.data.widgetType, { config: defaultConfig }, defaultConfig.title);
                this.focusWidget(evt.data.widgetType);
              }              
            }
            break;
          default:
            return;
        }
      },
      { passive: true }
    );
    this.componentsConfig.set('MapVecComponent', {
      canCloseWindow: true,
      canMaximize: true,
      canOpenInNewWindow: true,
      canPin: false,
      hasSubHeader: false,
      isPopoutWindow: false,
      title: 'Vue cartographique - VEC',
    });
    this.componentsConfig.set('MapEditComponent', {
      canCloseWindow: true,
      canMaximize: true,
      canOpenInNewWindow: true,
      canPin: false,
      hasSubHeader: false,
      isPopoutWindow: false,
      title: 'Vue cartographique - Edition',
    });
    this.componentsConfig.set('EsListComponent', {
      canCloseWindow: true,
      canMaximize: true,
      canOpenInNewWindow: true,
      canPin: false,
      hasSubHeader: false,
      isPopoutWindow: false,
      title: 'Evènements Spontanés',
    });
    this.componentsConfig.set('SpListComponent', {
      canCloseWindow: true,
      canMaximize: true,
      canOpenInNewWindow: true,
      canPin: false,
      hasSubHeader: true,
      isPopoutWindow: false,
      title: 'Scénarii Programmés',
    });
    this.componentsConfig.set('PgmListComponent', {
      canCloseWindow: true,
      canMaximize: true,
      canOpenInNewWindow: true,
      canPin: false,
      hasSubHeader: true,
      isPopoutWindow: false,
      title: 'PGM',
    });
    this.componentsConfig.set('EquipmentListComponent', {
      canCloseWindow: true,
      canMaximize: true,
      canOpenInNewWindow: true,
      canPin: false,
      hasSubHeader: false,
      isPopoutWindow: false,
      title: 'Equipements',
    });
    this.componentsConfig.set('AlarmeListComponent', {
      canCloseWindow: true,
      canMaximize: true,
      canOpenInNewWindow: true,
      canPin: false,
      hasSubHeader: false,
      isPopoutWindow: false,
      title: 'Alarmes',
    });
    this.componentsConfig.set('AvancementListComponent', {
      canCloseWindow: true,
      canMaximize: true,
      canOpenInNewWindow: true,
      canPin: false,
      hasSubHeader: false,
      isPopoutWindow: false,
      title: 'Avancements',
    });
    this.componentsConfig.set('LogListComponent', {
      canCloseWindow: true,
      canMaximize: true,
      canOpenInNewWindow: true,
      canPin: false,
      hasSubHeader: false,
      isPopoutWindow: false,
      title: 'Logs',
    });
    this.componentsConfig.set('HistoriqueComponent', {
      canCloseWindow: true,
      canMaximize: true,
      canOpenInNewWindow: true,
      canPin: false,
      hasSubHeader: false,
      isPopoutWindow: false,
      title: 'Historique',
    });
  }

  public initialize(elRef: ElementRef<HTMLElement>, appRef: ApplicationRef): void {
    if (!this.goldenLayout) {
      this.appRef = appRef;

      this.goldenLayout = new GoldenLayout(
        elRef.nativeElement,
        this.goldenLayoutBindComponentEventListener,
        this.goldenLayoutUnbindComponentEventListener
      );
      this.goldenLayout.resizeWithContainerAutomatically = true;

      this.goldenLayout.addEventListener('itemDestroyed', this.handleItemDestroyed.bind(this));
      this.goldenLayout.addEventListener('stateChanged', () => {
        console.debug('dashboard state changed');
        this.saveDashboardInStorage();
      });

      console.debug('dashboard service initialized');
    }
  }

  public openWidget(component: string, payload?: any): void {
    if (!this.componentsConfig.has(component)) {
      throw new Error(`The component name ${component} does not exist.`);
    }

    const config = this.componentsConfig.get(component);
    if (config) {
      config.item = payload;

      window.postMessage(
        {
          type: 'open_widget',
          config,
          widgetType: component,
        },
        window.location.href
      );
    }
  }

  public closeWidget(component: string) {
    if (!this.componentsConfig.has(component)) {
      throw new Error(`The component name ${component} does not exist.`);
    }

    if (this.goldenLayout && this.goldenLayout.rootItem) {
      const componentItem = this.findComponent(this.goldenLayout.rootItem.contentItems, component);
      if (componentItem) {
        componentItem.remove();
      }
    }
  }

  public isPopup() {
    return this.isCurrentWindowApopup;
  }

  public isWidgetOpen(name: string): boolean {
    return this.openedComponents.includes(name);
  }

  public focusWidget(name: string): void {
    if (this.goldenLayout.rootItem) {
      const componentItem = this.findComponent(this.goldenLayout.rootItem.contentItems, name);
      if (componentItem) {
        this.goldenLayout.focusComponent(componentItem);
      }
    }
  }

  private findComponent(items: ContentItem[], type: string): ComponentItem | undefined {
    if (items.length === 0) {
      return undefined;
    }

    for (const item of items) {
      if (!item.isComponent) {
        const found = this.findComponent(item.contentItems, type);
        if (found) {
          return found;
        }
      } else {
        if ((item as ComponentItem).componentType === type) {
          return item as ComponentItem;
        }
      }
    }

    return undefined;
  }

  public saveDashboard(): void {
    if (this.goldenLayout) {
      const config = this.goldenLayout.saveLayout();
      FileHelpers.save(JSON.stringify(config)).then(() => console.debug('saved'));
    }
  }

  private saveDashboardInStorage(): void {
    if (this.goldenLayout) {
      const config = this.goldenLayout.saveLayout();
      console.debug('saveDashboardInStorage', config);
      window.localStorage.setItem(this.LOCALSTORAGE_CONFIG_KEY, JSON.stringify(config));
    }
  }

  public loadDashboardInStorage(): boolean {
    try {
      const json = window.localStorage.getItem(this.LOCALSTORAGE_CONFIG_KEY);
      if (json) {
        const config = JSON.parse(json);
        this.loadDashboard(config);
        console.debug('loadlocaldash ok', config);
        return true;
      }
    } catch (error) {
      console.error('loadlocaldash', error);
    }

    return false;
  }

  public loadDashboard(savedGoldenLayoutConfig: LayoutConfig | ResolvedLayoutConfig): void {
    let config: LayoutConfig;
    if ('resolved' in savedGoldenLayoutConfig) {
      config = LayoutConfig.fromResolved(savedGoldenLayoutConfig);
    } else {
      config = savedGoldenLayoutConfig as unknown as LayoutConfig;
    }
    this.goldenLayout.loadLayout(config);
  }

  public addDragSource(element: HTMLDivElement, componentName: string): void {
    const config = this.componentsConfig.get(componentName);
    if (config) {
      const dragSourceConfig = () => {
        const item: DragSource.ComponentItemConfig = {
          state: {
            config,
          },
          title: config.title,
          type: componentName,
        };
        return item;
      };

      this.dragSources.push(this.goldenLayout.newDragSource(element, dragSourceConfig));
    }
  }

  public addComponent(componentType: string, forceNewTab: boolean = false): void {
    if (!this.componentsConfig.has(componentType)) {
      throw new Error(`Component ${componentType} has no config referenced.`);
    }

    if (this.isWidgetOpen(componentType) && !forceNewTab) {
      this.focusWidget(componentType);
      return;
    }

    const config = this.componentsConfig.get(componentType);
    if (config) {
      this.goldenLayout.addComponent(componentType, { config }, config.title);
    }
  }

  public registerOpenedWindow(windowId: string, windowType: IWindowType): void {
    if (!this.openedWindows[windowId]) {
      console.debug(`dashboard-service : add new window (${windowId} - ${windowType.type})`);
      this.openedWindows[windowId] = windowType;
    }
  }

  public removeDragSources(): void {
    for (const dragSource of this.dragSources) {
      if (dragSource) {
        this.goldenLayout.removeDragSource(dragSource);
      }
    }
  }

  public setSize(width: number, height: number): void {
    if (this.goldenLayout) {
      this.goldenLayout.setSize(width, height);
    }
  }

  private handleBindComponentEvent(
    container: ComponentContainer,
    itemConfig: ResolvedComponentItemConfig
  ): ComponentContainer.BindableComponent {
    const componentType = itemConfig.componentType;
    const componentRef = this.createComponent(componentType, container);
    const component = componentRef.instance;

    this.openedComponents.push(componentType as string);
    this.containerMap.set(container, componentRef);

    this.appRef.attachView(componentRef.hostView);

    const domElem = (componentRef.hostView as EmbeddedViewRef<unknown>).rootNodes[0] as HTMLElement;
    container.element.appendChild(domElem);

    container.layoutManager.on('itemDropped', (e) => {
      try {
        (e.component as WidgetHostDirective).handleHeaderConfiguration();
      } catch {}
    });

    return {
      component,
      virtual: false,
    };
  }

  private handleUnbindComponentEvent(container: ComponentContainer): void {
    setTimeout(() => {
      const element = container.parent?.parent?.element;
      if (element) {
        const popoutElement = element.querySelector('div.lm_popout') as HTMLDivElement | null;
        if (popoutElement) {
          popoutElement.style.display = 'none';
        }
      }
    }, 10);

    const componentRef = this.containerMap.get(container);
    if (componentRef === undefined) {
      throw new Error('handleUnbindComponentEvent: Component not found');
    }
    this.containerMap.delete(container);

    const hostView = componentRef.hostView;
    const component = componentRef.instance;
    const componentRootElement = component.rootHtmlElement;
    container.element.removeChild(componentRootElement);
    this.appRef.detachView(hostView);

    componentRef.destroy();
  }

  private handleItemDestroyed(evt: BubblingEvent): void {
    if (evt.target instanceof ComponentItem) {
      const index = this.openedComponents.indexOf(evt.target.componentType as string);
      if (index > -1) {
        this.openedComponents.splice(index, 1);
      }
    }
  }

  public registerComponent(componentName: string, componentType: Type<any>): void {
    if (this.components.has(componentName)) {
      return;
    }
    this.components.set(componentName, componentType);
  }

  private createComponent(config: (() => DragSource.ComponentItemConfig) | JsonValue, container: ComponentContainer): ComponentRef<any> {
    const componentName = typeof config === 'function' ? (config().type as string) : (config as string);

    console.debug('createComponent', config, componentName, this.components);

    const componentType = this.components.get(componentName);

    if (!componentType) {
      throw new Error(`Unknown component type from name "${componentName}"`);
    }

    if (container.state) {
      // @ts-ignore
      container.state.config.id = v4();
    }

    const provider: StaticProvider = {
      provide: GoldenLayoutContainerInjectionToken,
      useValue: container,
    };
    const injector = Injector.create({
      providers: [provider],
    });
    const componentFactoryRef = this.componentFactoryResolver.resolveComponentFactory<any>(componentType);
    return componentFactoryRef.create(injector);
  }
}
